import { useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import { ASSETS } from "../../../../utils/config";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import { resolveSocialIcon } from "../../../../utils/socialHelpers";
import SocialLinksSettings from "./SocialLinksSettings";
import useApplyGlobalSettings, { CODEBLOCK_TYPES } from "../../../../hooks/useApplyGlobalSettings";

const SocialLinksWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ iconsPerRow }) => iconsPerRow || 1}, 1fr);
  gap: 5px;
  width: max-content;
`;

const IconWrapper = styled.div`
  margin: 5px;
  cursor: ${({ isLinkValid }) => (isLinkValid ? "pointer" : "not-allowed")};
`;

const Image = styled.img`
  width: 100%;
`;

export const SocialLinks = ({
  socialLinks,
  alignment,
  iconsPerRow,
  allSideMargin,
  isApplyMarginOnAllSides,
  margin,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();
  
  const dispatch = useDispatch();
  const { socialMediaLinks: socialMediaLinksProps } = useSelector((state) => state.codeblockStyles);

  // Initialize with default props from reducer for new components
  useEffect(() => {
    // Set the props from the reducer if they're undefined
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Social media specific properties
        if (props.socialLinks === undefined) {
          props.socialLinks = [...socialMediaLinksProps.socialLinks];
        }
        
        if (props.alignment === undefined) {
          props.alignment = socialMediaLinksProps.alignment;
        }
        
        if (props.iconsPerRow === undefined) {
          props.iconsPerRow = socialMediaLinksProps.iconsPerRow;
        }
        
        // Margin properties
        if (props.margin === undefined) {
          props.margin = { ...socialMediaLinksProps.margin };
        }
        
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = socialMediaLinksProps.isApplyMarginOnAllSides;
        }
        
        if (props.allSideMargin === undefined) {
          props.allSideMargin = socialMediaLinksProps.allSideMargin;
        }
      });
    }, 5);
    
    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const filteredLinks = socialLinks.filter((link) => link.platform);

  const onClickIcon = (link) => {
    if (!link || typeof link !== 'string') return;
    
    let url = link.trim();

    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    window.open(url, "_blank");
  };

  // Use the hook to apply global settings when they change
  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.SocialMediaLinks);

  const renderIcons = () => {
    return filteredLinks.map((link, index) => {
      const IconComponent = resolveSocialIcon(link);

      // Ensure link is a string and not null/undefined
      const isValidLink = link && typeof link.link === 'string' && link.link.trim() !== '';

      return (
        <IconWrapper
          key={link.id || index}
          isLinkValid={link.isLinkValid && isValidLink}
          onClick={() => link.isLinkValid && isValidLink && onClickIcon(link.link)}
        >
          {React.createElement(IconComponent, {
            width: 24,
          })}
        </IconWrapper>
      );
    });
  };

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: alignment || "center",
          }}
        >
          <SocialLinksWrapper
            alignment={alignment}
            iconsPerRow={
              iconsPerRow < filteredLinks.length
                ? iconsPerRow
                : filteredLinks.length
            }
            style={{
              margin: marginStyle,
            }}
          >
            {filteredLinks.length ? (
              renderIcons()
            ) : (
              <Image src={ASSETS.segmentEditor.DummySocial} />
            )}
          </SocialLinksWrapper>
        </div>
      </BlockIndicator>
    </div>
  );
};

export const SocialLinksDefaultProps = {
  socialLinks: [],
  alignment: undefined,
  iconsPerRow: undefined,
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
};

SocialLinks.craft = {
  props: SocialLinksDefaultProps,
  related: {
    settings: SocialLinksSettings,
  },
};
